import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Button } from "react-bootstrap";
import { ThreeDotsVertical } from "react-bootstrap-icons";
import type { CaseFileLite, CaseFileSearchResult } from "@/briefpoint-client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/toast";
import { useCaseFileApi } from "@/hooks/useApi";
import { searchCaseFilesQueryOptions } from "@/lib/query-options";
import styles from "./CaseFileItem.module.scss";

interface Props {
  file: CaseFileLite;
  selectedFile: string | null;
  onSelect: (fileId: string) => void;
}

export default function CaseFileItem(props: Props) {
  const caseFileApi = useCaseFileApi();
  const queryClient = useQueryClient();
  const searchParams = new URLSearchParams(location.search);
  const search = searchParams.get("search");

  const searchQueryOptions = searchCaseFilesQueryOptions(caseFileApi, {
    caseId: props.file.caseId || "",
    // If we are searching, don't pass a folderId
    folderId: search ? undefined : props.file.folderId || undefined,
    search: search || undefined,
  });

  const refetchSearchCaseFilesQuery = () => {
    queryClient.invalidateQueries(searchQueryOptions);
  };

  //MUTATIONS
  const setMoveProduceMutation = useMutation({
    mutationFn: async () => {
      if (!props.file.caseId) throw new Error("Case ID is required");
      return Promise.all([
        caseFileApi.caseFileSetPrivileged({
          fileId: props.file.id,
          caseId: props.file.caseId || "",
          body: false,
        }),
        caseFileApi.caseFileSetWithheld({
          fileId: props.file.id,
          caseId: props.file.caseId || "",
          body: false,
        }),
      ]);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: searchQueryOptions.queryKey,
      });

      const previousData = queryClient.getQueryData(
        searchQueryOptions.queryKey,
      );

      queryClient.setQueryData(
        searchQueryOptions.queryKey,
        (old: CaseFileSearchResult | undefined) => {
          if (!old) return old;
          return {
            ...old,
            files: old.files?.map((file) =>
              file.id === props.file.id
                ? { ...file, isPrivileged: false, isWithheld: false }
                : file,
            ),
          };
        },
      );

      return { previousData };
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: searchQueryOptions.queryKey,
      });
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          searchQueryOptions.queryKey,
          context.previousData,
        );
      }
      toast.error(`Error moving ${props.file.name} to Produce`);
    },
  });

  // Change Withheld Flag
  const setWithheldMutation = useMutation({
    mutationFn: async (isWithheld: boolean) => {
      if (!props.file.caseId) throw new Error("Case ID is required");
      return caseFileApi.caseFileSetWithheld({
        fileId: props.file.id,
        caseId: props.file.caseId || "",
        body: isWithheld,
      });
    },
    onMutate: async (isWithheld) => {
      await queryClient.cancelQueries({
        queryKey: searchQueryOptions.queryKey,
      });

      const previousData = queryClient.getQueryData(
        searchQueryOptions.queryKey,
      );

      queryClient.setQueryData(
        searchQueryOptions.queryKey,
        (old: CaseFileSearchResult | undefined) => {
          const { files, folders } = old || { files: [], folders: [] };
          return {
            files: files.map((file) =>
              file.id === props.file.id
                ? { ...file, isWithheld: isWithheld }
                : file,
            ),
            folders: folders.map((folder) =>
              folder.id === props.file.folderId
                ? { ...folder, isWithheld: isWithheld }
                : folder,
            ),
          };
        },
      );

      return { previousData };
    },
    onSuccess: (_, isWithheld) => {
      toast.success(
        isWithheld
          ? `${props.file.name} was designated as Withheld`
          : `${props.file.name} was moved to Produce`,
        {
          action: {
            label: "Undo",
            onClick: () => {
              setWithheldMutation.mutate(!isWithheld);
            },
          },
        },
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: searchQueryOptions.queryKey,
      });
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          searchQueryOptions.queryKey,
          context.previousData,
        );
      }
      toast.error(`Error designating ${props.file.name} as Withheld`);
    },
  });

  // Change Privileged Flag
  const setPrivilegedMutation = useMutation({
    mutationFn: async (isPrivileged: boolean) => {
      if (!props.file.caseId) throw new Error("Case ID is required");
      return caseFileApi.caseFileSetPrivileged({
        fileId: props.file.id,
        caseId: props.file.caseId || "",
        body: isPrivileged,
      });
    },
    onMutate: async (isPrivileged) => {
      await queryClient.cancelQueries({
        queryKey: searchQueryOptions.queryKey,
      });

      const previousData = queryClient.getQueryData(
        searchQueryOptions.queryKey,
      );

      queryClient.setQueryData(
        searchQueryOptions.queryKey,
        (old: CaseFileSearchResult | undefined) => {
          const { files, folders } = old || { files: [], folders: [] };
          return {
            files: files.map((file) =>
              file.id === props.file.id
                ? { ...file, isPrivileged: isPrivileged }
                : file,
            ),
            folders: folders.map((folder) =>
              folder.id === props.file.folderId
                ? { ...folder, isPrivileged: isPrivileged }
                : folder,
            ),
          };
        },
      );

      return { previousData };
    },
    onSuccess: (_, isPrivileged) => {
      toast.success(
        isPrivileged
          ? `${props.file.name} was designated as Privileged`
          : `${props.file.name} was moved to Produce`,
        {
          action: {
            label: "Undo",
            onClick: () => {
              setMoveProduceMutation.mutate();
            },
          },
        },
      );
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          searchQueryOptions.queryKey,
          context.previousData,
        );
      }
      toast.error(`Error designating ${props.file.name} as Privileged`);
    },
    onSettled: () => {
      refetchSearchCaseFilesQuery();
    },
  });

  // Delete File
  const deleteFileMutation = useMutation({
    mutationFn: async () => {
      if (!props.file.caseId) throw new Error("Case ID is required");
      return caseFileApi.caseFileDelete({
        fileId: props.file.id,
        caseId: props.file.caseId || "",
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: searchQueryOptions.queryKey,
      });

      const previousData = queryClient.getQueryData(
        searchQueryOptions.queryKey,
      );

      queryClient.setQueryData(
        searchQueryOptions.queryKey,
        (old: CaseFileSearchResult | undefined) => {
          const { files, folders } = old || { files: [], folders: [] };
          return {
            files: files.filter((file) => file.id !== props.file.id),
            folders,
          };
        },
      );

      return { previousData };
    },
    onSuccess: () => {
      toast.success(`${props.file.name} was deleted`);
    },
    onError: (_, __, context) => {
      // Restore previous data on error
      if (context?.previousData) {
        queryClient.setQueryData(
          searchQueryOptions.queryKey,
          context.previousData,
        );
      }
      toast.error("Error deleting file");
    },
    onSettled: () => {
      refetchSearchCaseFilesQuery();
    },
  });

  return (
    <div
      className={clsx(
        styles.file,
        props.selectedFile === props.file.id && styles.selected,
      )}
      key={props.file.id}
    >
      <Button
        variant="link"
        className={"ps-2 text-decoration-none"}
        onClick={() => {
          props.onSelect(props.file.id);
        }}
      >
        <div className={styles.name} title={props.file.name}>
          {props.file.name}
        </div>

        <div
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          className={styles.actions}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild className={styles.trigger}>
              <ThreeDotsVertical size={14} color="var(--text)" />
            </DropdownMenuTrigger>
            <DropdownMenuPortal>
              <DropdownMenuContent>
                <DropdownMenuItem
                  className={styles.dropDownOption}
                  onClick={() =>
                    setPrivilegedMutation.mutate(!props.file.isPrivileged)
                  }
                >
                  {props.file.isPrivileged
                    ? "Remove Privileged Status"
                    : "Designate as Privileged"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={styles.dropDownOption}
                  onClick={() =>
                    setWithheldMutation.mutate(!props.file.isWithheld)
                  }
                >
                  {props.file.isWithheld
                    ? "Remove Withheld Status"
                    : "Designate as Withheld"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={`${styles.dropDownOption} ${styles.deleteOption}`}
                  onClick={() => deleteFileMutation.mutate()}
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenuPortal>
          </DropdownMenu>
        </div>
      </Button>
    </div>
  );
}
